import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, ArrowRight, Check, Plus, Upload, X } from 'lucide-react';
import { isSpreadsheet, spreadsheetToText } from '@/lib/spreadsheet';
import { createTournament, reduce, preset, PRESETS, type ActionBody, type PresetName, type RuleSet } from '@airmail/engine';
import { importClubText, importText } from '@airmail/io';
import { pairingKey, partnerMatchCount, teamKey } from '@airmail/engine';
import { newId, saveTournament } from '@/store/db';
import { lineupText, listSeasons, playedPairings, seasonKader, type Season } from '@/store/season';
import { useAuth } from '@/store/AuthProvider';
import { DEFAULT_API, enableSyncFor } from '@/store/sync';
import { Button, Field, Input, Pill, Select, Textarea, cx } from '@/ui';
import { DEFAULT_MODE_OPTIONS, KO_TEAM_CHOICES, MODES_GEORDNET, groupSizeChoices, modeInfo, phaseConfigsFor, previewMode, type ModeInfo, type ModeKey, type ModeOptions } from '@/lib/modes';
import { fmtDateInput, fmtMinutes } from '@/lib/format';
import { useT } from '@/i18n';
import { Darstellung } from '@/components/Darstellung';
import { useTm } from '@/i18n/engine';
import { RuleEditor } from '@/components/RuleEditor';

const STEPS = ['Turnier', 'Modus', 'Regeln', 'Felder', 'Teams'] as const;

const PRESET_LABELS: Record<PresetName, string> = {
  tournament: 'Turnier (ECO/ACL): 21, kein Bust, Sieg ab 21',
  tournamentShort: 'Turnier kurz: 15 Punkte, 8 Innings / 15 min',
  fun: 'Freizeit: 21, Bust auf 11, Sieg mit 2',
  league: 'Liga-Abend: Best-of-3, 20 min',
  singles: 'Singles: 21, ein Spieler je Team',
};

export function NewTournament() {
  const t = useT();
  const tm = useTm();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [location, setLocation] = useState('');
  const [mode, setMode] = useState<ModeKey>('groups_ko');
  const [modeOpts, setModeOpts] = useState<ModeOptions>(DEFAULT_MODE_OPTIONS);
  const [presetName, setPresetName] = useState<PresetName>('tournament');
  const [rules, setRules] = useState<RuleSet>(preset('tournament'));

  // Mehrere Spielorte (docs/notes/spielorte.md): „Piazza“ mit 5 Feldern, „Sportplatz“ mit 4.
  // Leer = wie bisher, dann zählt nur die Zahl oben und die Felder heißen „Feld 1 … n“.
  /*
   * Felder als eine einzige Liste — Zeile = ein Ort mit seiner Feldzahl. Der
   * Name darf leer bleiben; dann heißen die Felder schlicht „Feld 1 … N".
   * Vorher standen hier zwei Eingaben übereinander („Anzahl Felder" und
   * darunter „Mehrere Spielorte"), von denen die obere gesperrt wurde, sobald
   * man unten etwas eintrug. Daniel: „das ist verwirrend."
   */
  const [venues, setVenues] = useState<{ name: string; count: number }[]>([{ name: '', count: 4 }]);
  const [teamText, setTeamText] = useState('');
  const [busy, setBusy] = useState(false);
  const auth = useAuth();
  const cloudPossible = !!auth.user && !!DEFAULT_API;
  const [cloud, setCloud] = useState(true);
  const [seasons, setSeasons] = useState<Season[]>([]);
  // Saison, aus der der Kader kam: Begegnungen, die dort schon gespielt sind, werden nicht noch einmal ausgelost (docs/13 §4)
  const [seasonId, setSeasonId] = useState<string>('');
  useEffect(() => {
    listSeasons()
      .then(setSeasons)
      .catch(() => setSeasons([]));
  }, []);

  const fileRef = useRef<HTMLInputElement>(null);
  const [fileInfo, setFileInfo] = useState<string | null>(null);
  const onFile = async (file: File) => {
    try {
      if (isSpreadsheet(file)) {
        const r = await spreadsheetToText(file);
        const sheet = r.sheets.find((s) => s.tsv.trim() !== '');
        if (!sheet) return setFileInfo(t('Keine Daten in der Datei gefunden'));
        setTeamText(sheet.tsv);
        setFileInfo(
          r.sheets.length > 1
            ? t('{file} · Blatt „{sheet}“ (von {n})', { file: file.name, sheet: sheet.name, n: r.sheets.length })
            : t('{file} · Blatt „{sheet}“', { file: file.name, sheet: sheet.name }),
        );
      } else {
        setTeamText(await file.text());
        setFileInfo(file.name);
      }
    } catch (e) {
      setFileInfo(t('Datei konnte nicht gelesen werden: {e}', { e: (e as Error).message }));
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const fileButton = (
    <div className="flex items-center gap-3">
      <Button onClick={() => fileRef.current?.click()}>
        <Upload size={14} /> {t('Excel- oder CSV-Datei')}
      </Button>
      <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls,.ods,.csv,.tsv,.txt" hidden onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])} />
      <span className="text-xs text-dim">{fileInfo ?? t('oder unten einfügen / tippen')}</span>
    </div>
  );

  const clubMode = mode === 'club_rr';
  // Saisons mit Kader (docs/13 §4): Plätze je Mannschaft mit den Personen, die zuletzt darin spielten
  const rosters = useMemo(() => seasons.map((s) => ({ season: s, kader: seasonKader(s) })).filter((x) => x.kader.length > 0), [seasons]);
  const parsed = useMemo(() => (!clubMode && teamText.trim() ? importText(teamText, { teamSize: rules.teamSize }) : null), [teamText, rules.teamSize, clubMode]);
  const clubParsed = useMemo(() => (clubMode && teamText.trim() ? importClubText(teamText, rules.teamSize) : null), [teamText, rules.teamSize, clubMode]);
  const teamCount = clubMode ? (clubParsed?.teams.length ?? 0) : (parsed?.teams.length ?? 0);
  const saison = seasons.find((s) => s.id === seasonId);
  const schonGespielt = useMemo(() => {
    if (!clubMode || !saison || !clubParsed) return { keys: [] as string[], count: 0 };
    const done = playedPairings(saison);
    if (done.size === 0) return { keys: [], count: 0 };
    const keys = clubParsed.teams.map((x) => teamKey({ name: x.name ?? '', ...(x.club ? { club: x.club } : {}) }));
    let count = 0;
    for (let i = 0; i < keys.length; i++) for (let j = i + 1; j < keys.length; j++) if (done.has(pairingKey(keys[i] as string, keys[j] as string))) count++;
    return { keys: [...done], count };
  }, [clubMode, saison, clubParsed]);
  // Gültige Orte (Name gesetzt) bestimmen die Felder; ohne solche bleibt es bei der Zahl
  const venueRows = venues.filter((v) => v.count >= 1);
  // Mindestens ein Feld, auch wenn jemand alle Zeilen löscht
  const courtTotal = Math.max(
    1,
    venueRows.reduce((s, v) => s + v.count, 0),
  );
  const benannt = venueRows.filter((v) => v.name.trim() !== '');
  const venueLine = benannt.length > 0 ? benannt.map((v) => `${v.name.trim()} ${v.count}`).join(' · ') : undefined;
  const preview = previewMode(
    mode,
    modeOpts,
    teamCount || (clubMode ? 12 : 16),
    courtTotal,
    rules.plannedMatchMinutes,
    clubParsed?.clubs.map((c) => c.pairs),
    clubParsed?.teams.map((x) => ({ name: x.name ?? '', ...(x.club ? { club: x.club } : {}) })),
  );

  /** Wechselnde Partner: gemeldet wird einzeln — Regeln auf Singles, Gruppen ab 4 */
  const waehleModus = (m: ModeKey) => {
    setMode(m);
    if (m === 'partner_ko') {
      setPresetName('singles');
      setRules(PRESETS.singles());
      if (modeOpts.groupSize < 4) setModeOpts({ ...modeOpts, groupSize: 4 });
    }
  };

  const canNext = step === 0 ? name.trim().length > 0 : true;

  const finish = async () => {
    setBusy(true);
    const id = newId();
    let tour = createTournament({ id, name: name.trim(), seed: id, createdAt: Date.now() });
    const run = (body: ActionBody) => {
      const r = reduce(tour, { ...body, at: Date.now() });
      if (!r.ok) throw new Error(r.error);
      tour = r.state;
    };
    run({ type: 'tournament.update', patch: { date, location: location.trim() || undefined } });
    // Bewerbs- und Feldnamen sind Daten: Sie entstehen einmal in der Sprache, in der angelegt wird
    run({
      type: 'competition.add',
      name: clubMode ? t('Mannschaften') : rules.teamSize === 1 ? t('Singles') : t('Doubles'),
      // Liga und wechselnde Partner: ein Sieg = 1 Punkt (Harald: „Laß uns die 1 immer als Siege bezeichnen“)
      ruleSet: clubMode || mode === 'partner_ko' ? { ...rules, points: { win: 1, draw: 0, loss: 0 }, draws: 'never' } : rules,
    });
    const comp = tour.competitions[0];
    if (!comp) throw new Error('kein Bewerb');
    // Zeile mit Namen → „Piazza 1 … 5" mit Ort; Zeile ohne Namen → „Feld 1 … N"
    let ohneOrt = 0;
    for (const v of venueRows.length > 0 ? venueRows : [{ name: '', count: 4 }]) {
      const ort = v.name.trim();
      for (let i = 1; i <= v.count; i++) {
        if (ort) run({ type: 'court.add', name: `${ort} ${i}`, group: ort });
        else run({ type: 'court.add', name: t('Feld {n}', { n: ++ohneOrt }) });
      }
    }
    for (const cfg of phaseConfigsFor(mode, modeOpts))
      run({ type: 'phase.add', competitionId: comp.id, config: cfg.type === 'groups' && clubMode && schonGespielt.keys.length > 0 ? { ...cfg, skipPairings: schonGespielt.keys } : cfg });
    if (clubMode) for (const team of clubParsed?.teams ?? []) run({ type: 'team.add', competitionId: comp.id, team });
    else for (const it of parsed?.teams ?? []) if (!it.issues.some((i) => i.level === 'error')) run({ type: 'team.add', competitionId: comp.id, team: it.team });
    await saveTournament(tour);
    if (cloudPossible && cloud) {
      // Turnier gehört ab jetzt dem Konto (docs/16) — schlägt das fehl (offline), bleibt es lokal und lässt sich später sichern
      try {
        await enableSyncFor(tour);
      } catch (e) {
        console.warn('Cloud-Sicherung beim Anlegen fehlgeschlagen', e);
      }
    }
    nav(`/t/${id}/teams`);
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/" className="label no-underline hover:text-ink inline-flex items-center gap-1">
          <ArrowLeft size={12} /> {t('Turnierliste')}
        </Link>
        {/* Sprache auf jeder Seite (Harald, 10/2026) */}
        <Darstellung className="ml-auto" />
      </div>
      <ol className="flex gap-1 m-0 p-0 list-none mb-8">
        {STEPS.map((s, i) => (
          <li key={s} className={cx('flex-1 border-b-2 pb-2 label', i === step ? 'border-red text-ink' : i < step ? 'border-live text-live' : 'border-line')}>
            {i + 1} · {t(s)}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="grid gap-4">
          <h2 className="text-lg font-bold m-0">{t('Wie heißt das Turnier?')}</h2>
          <Field label={t('Name')}>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Ulm Open 2026" onKeyDown={(e) => e.key === 'Enter' && canNext && setStep(1)} />
          </Field>
          <div className="field-row grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('Datum')} hint={fmtDateInput(date) || undefined}>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label={t('Ort (optional)')}>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t('Sporthalle Süd')} />
            </Field>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-4">
          <h2 className="text-lg font-bold m-0">{t('Wie wird gespielt?')}</h2>
          <ModusWahl value={mode} onChange={waehleModus} />
          {mode !== 'ko' && mode !== 'club_rr' && (
            <div className="field-row grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(mode === 'groups_ko' || mode === 'partner_ko') && (
                <Field label={t('Gruppengröße')} {...(mode === 'partner_ko' ? { hint: t('mindestens — wer übrig bleibt, kommt in eine der Gruppen dazu') } : {})}>
                  <Select value={modeOpts.groupSize} onChange={(e) => setModeOpts({ ...modeOpts, groupSize: Number(e.target.value) })}>
                    {groupSizeChoices(mode).map((n) => (
                      <option key={n} value={n}>
                        {mode === 'partner_ko' ? t('{n} Spieler', { n }) : t('{n} Teams', { n })}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {mode === 'partner_ko' && (
                <Field label={t('Partner')} hint={t('Standard: jedes Paar spielt genau einmal zusammen. Viel länger: jedes Paar gegen jedes andere Paar („jeder mit jedem gegen jeden mit jedem“).')}>
                  <Select value={modeOpts.partners} onChange={(e) => setModeOpts({ ...modeOpts, partners: e.target.value === 'all' ? 'all' : 'once' })} data-partner-modus>
                    <option value="once">{t('jedes Paar einmal zusammen ({n} Spiele bei 5)', { n: partnerMatchCount(5, 'once') })}</option>
                    <option value="all">{t('jedes Paar gegen jedes Paar ({n} Spiele bei 5)', { n: partnerMatchCount(5, 'all') })}</option>
                  </Select>
                </Field>
              )}
              {(mode === 'groups_ko' || mode === 'partner_ko') && (
                <Field label={t('Weiter je Gruppe')}>
                  <Select value={modeOpts.qualifiersPerGroup} onChange={(e) => setModeOpts({ ...modeOpts, qualifiersPerGroup: Number(e.target.value) })}>
                    {[1, 2, 3].map((n) => (
                      <option key={n} value={n}>
                        {t('Top {n}', { n })}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {(mode === 'groups_ko' || mode === 'partner_ko') && (
                <Field label={t('Dazu beste Nächstplatzierte')} hint={t('füllt das KO auf, z. B. 6 Gruppen × Top 2 + 4 beste Dritte = 16')}>
                  <Select value={modeOpts.bestRemaining} onChange={(e) => setModeOpts({ ...modeOpts, bestRemaining: Number(e.target.value) })}>
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>
                        {n === 0 ? t('keine') : n}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {mode === 'rr_ko' && (
                <Field label={t('Weiter ins KO')} hint={t('die Bestplatzierten der Vorrunde')}>
                  <Select value={modeOpts.koTeams} onChange={(e) => setModeOpts({ ...modeOpts, koTeams: Number(e.target.value) })}>
                    {KO_TEAM_CHOICES.map((n) => (
                      <option key={n} value={n}>
                        {t('Top {n}', { n })}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label={t('Vereinskollegen trennen')}>
                <Select value={modeOpts.avoidSameClub ? '1' : '0'} onChange={(e) => setModeOpts({ ...modeOpts, avoidSameClub: e.target.value === '1' })}>
                  <option value="1">{t('ja, wenn möglich')}</option>
                  <option value="0">{t('egal')}</option>
                </Select>
              </Field>
            </div>
          )}
          {mode === 'club_rr' && (
            <Field
              label={t('Gruppen')}
              hint={t(
                'Standard: alle in einer Gruppe, jedes Paar gegen alle Paare der anderen Mannschaften. Leistungsgruppen: „Ceva 1“, „Nucetto 1“, „Mombarcaro 1“ … spielen in Gruppe 1 jeder gegen jeden, die Zweier in Gruppe 2 — wie Medenspiele im Tennis. Die Nummer meldet der Teamleiter der Mannschaft.',
              )}
            >
              <Select value={modeOpts.leistungsgruppen ? '1' : '0'} onChange={(e) => setModeOpts({ ...modeOpts, leistungsgruppen: e.target.value === '1' })} data-leistungsgruppen>
                <option value="0">{t('eine Gruppe: jedes Paar gegen alle anderen Mannschaften')}</option>
                <option value="1">{t('Leistungsgruppen: gleiche Nummer spielt gegeneinander')}</option>
              </Select>
            </Field>
          )}
          {mode !== 'roundrobin' && mode !== 'club_rr' && (
            <Field label={t('Spiel um Platz 3')}>
              <Select value={modeOpts.thirdPlaceMatch ? '1' : '0'} onChange={(e) => setModeOpts({ ...modeOpts, thirdPlaceMatch: e.target.value === '1' })}>
                <option value="1">{t('ja')}</option>
                <option value="0">{t('nein')}</option>
              </Select>
            </Field>
          )}
          <Preview lines={preview.lines} minutes={preview.minutes} courts={courtTotal} teams={teamCount} venues={venueLine} />
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-4">
          <h2 className="text-lg font-bold m-0">{t('Nach welchen Regeln?')}</h2>
          <Field label={t('Vorlage')}>
            <Select
              value={presetName}
              onChange={(e) => {
                const p = e.target.value as PresetName;
                setPresetName(p);
                setRules(PRESETS[p]());
              }}
            >
              {(Object.keys(PRESET_LABELS) as PresetName[]).map((p) => (
                <option key={p} value={p}>
                  {t(PRESET_LABELS[p])}
                </option>
              ))}
            </Select>
          </Field>
          <RuleEditor rules={rules} onChange={setRules} />
        </div>
      )}

      {step === 3 && (
        <div className="grid gap-4">
          <h2 className="text-lg font-bold m-0">{t('Wie viele Felder?')}</h2>
          <div className="grid gap-2">
            <span className="label">{t('Spielort und Anzahl Felder')}</span>
            {venues.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex-1">
                  <Input
                    placeholder={t('z. B. Piazza — leer lassen, wenn es nur einen Ort gibt')}
                    aria-label={t('Name des Spielorts')}
                    value={v.name}
                    onChange={(e) => setVenues(venues.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  />
                </div>
                <div className="w-24 shrink-0">
                  <Input
                    type="number"
                    min={1}
                    max={32}
                    aria-label={t('Anzahl Felder')}
                    value={v.count}
                    onChange={(e) => setVenues(venues.map((x, j) => (j === i ? { ...x, count: Math.max(1, Math.min(32, Number(e.target.value) || 1)) } : x)))}
                  />
                </div>
                <Button variant="ghost" title={t('Spielort entfernen')} disabled={venues.length <= 1} onClick={() => setVenues(venues.filter((_, j) => j !== i))}>
                  <X size={14} />
                </Button>
              </div>
            ))}
            <div>
              <Button onClick={() => setVenues([...venues, { name: '', count: 4 }])}>
                <Plus size={14} /> {t('Weiterer Spielort')}
              </Button>
            </div>
            <span className="text-xs text-dim">
              {t(
                'Ohne Ortsnamen heißen die Felder „Feld 1 … {n}“. Mit Namen — „Piazza“ mit 5 Feldern, „Sportplatz“ mit 4 — heißen sie „Piazza 1 … 5“ und „Sportplatz 1 … 4“, und jede Gruppe lässt sich später einem Ort zuordnen. Namen und Priorität sind jederzeit unter „Felder“ änderbar.',
                { n: courtTotal },
              )}
            </span>
          </div>
          <Preview lines={preview.lines} minutes={preview.minutes} courts={courtTotal} teams={teamCount} venues={venueLine} />
        </div>
      )}

      {step === 4 && (
        <div className="grid gap-4">
          <h2 className="text-lg font-bold m-0">{t('Wer spielt mit?')}</h2>
          {clubMode ? (
            <>
              {rosters.length > 0 && (
                <Field
                  label={t('Kader aus Saison übernehmen')}
                  hint={t(
                    'Füllt die Liste mit dem Kader der Saison — Teamnamen bleiben gleich, Personen dürfen wechseln. Alle bleiben drin: Am Spieltag zählt der Check-in, ausgelost werden nur eingecheckte Teams. Begegnungen, die in der Saison schon gespielt sind, werden nicht noch einmal ausgelost.',
                  )}
                >
                  <Select
                    value=""
                    onChange={(e) => {
                      const r = rosters.find((x) => x.season.id === e.target.value);
                      if (!r) return;
                      // Mit Teamnamen — so erkennt die Saison „Ceva 1" wieder; wer heute fehlt, bleibt drin und wird einfach nicht eingecheckt
                      setTeamText(lineupText(r.kader));
                      setSeasonId(r.season.id);
                    }}
                  >
                    <option value="">{t('– Saison wählen –')}</option>
                    {rosters.map((r) => (
                      <option key={r.season.id} value={r.season.id}>
                        {t('{season} · {n} Teams aus {c} Mannschaften', { season: r.season.name, n: r.kader.length, c: new Set(r.kader.map((k) => k.club ?? '')).size })}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {fileButton}
              <Field
                label={t('Mannschaften und Spieler')}
                hint={t(
                  'Eine Zeile je Paar: „Ceva: Anna Rossi; Luca Bianchi“. Oder eine Zeile je Spieler: „Ceva: Anna Rossi“ — dann bilden je zwei aufeinanderfolgende Zeilen ein Paar. Aus Excel: Spalten Mannschaft | Spieler 1 | Spieler 2 einfach kopieren. Hat eine Mannschaft mehrere Paare, heißen sie „Ceva 1“, „Ceva 2“ … Eigene Teamnamen bleiben, wie sie sind: „Ceva 1; Anna; Luca“ oder mit vierter Spalte „Bag Brothers; Anna; Luca; Ceva“. Ersatzspieler mit Stern: „*Max Muster“ (zählt für Paar und Mannschaft, nicht in der Spielerwertung).',
                )}
              >
                <Textarea
                  rows={12}
                  value={teamText}
                  onChange={(e) => setTeamText(e.target.value)}
                  placeholder={'Ceva: Anna Rossi; Luca Bianchi\nCeva: Paolo Verdi; *Max Muster\nNucetto: Gina Russo; Sara Conti\nNucetto: Marco Neri; Elena Costa\n…'}
                />
              </Field>
            </>
          ) : (
            <>
              {fileButton}
              <Field
                label={t('Teams')}
                hint={t(
                  'Eine Zeile je Team, drei Schreibweisen gehen: „Bag Brothers: Daniel Roth; Max Müller“ (Doppelpunkt nach dem Team), „Bag Brothers; Daniel Roth; Max Müller“ (Semikolon oder Tab, wie aus Excel kopiert) oder nur „Daniel Roth / Max Müller“ (Teamname wird aus den Nachnamen gebildet). Vornamen allein reichen. Zwei Zeilen mit demselben Teamnamen und je einem Spieler werden ein Team. Überschriften dürfen mit drin sein. Kann auch leer bleiben — Teams gibt es später unter „Teams“ (auch Excel-Datei mit Spalten-Mapping und Online-Anmeldung).',
                )}
              >
                <Textarea
                  rows={10}
                  value={teamText}
                  onChange={(e) => setTeamText(e.target.value)}
                  placeholder={'Bag Brothers: Daniel Roth; Max Müller\nHole in None; Lisa Schmitt; Tom Berger\nSara Conti / Gina Russo\nDie Werfer; Peter\nDie Werfer; Harald'}
                />
              </Field>
            </>
          )}
          {clubParsed && (
            <div className="text-sm text-muted">
              <b className="text-ink">{t('{c} Mannschaften · {p} Paare erkannt', { c: clubParsed.clubs.length, p: clubParsed.teams.length })}</b>
              <ul className="m-0 mt-2 pl-4">
                {clubParsed.clubs.map((c) => {
                  const subs = clubParsed.teams
                    .filter((x) => x.club === c.name)
                    .flatMap((x) => x.players)
                    .filter((p) => p.substitute).length;
                  return (
                    <li key={c.name}>
                      {subs > 0 ? t('{name}: {n} Spieler (davon {s} Ersatz)', { name: c.name, n: c.players, s: subs }) : t('{name}: {n} Spieler', { name: c.name, n: c.players })} →{' '}
                      {t('{n} Paare', { n: c.pairs })} (
                      {clubParsed.teams
                        .filter((x) => x.club === c.name)
                        .map((x) => x.name)
                        .join(', ')}
                      )
                    </li>
                  );
                })}
              </ul>
              {saison && schonGespielt.count > 0 && (
                <div className="text-live mt-1" data-saison-skip>
                  {t('{n} Begegnungen sind in „{season}“ schon gespielt und werden nicht noch einmal ausgelost.', { n: schonGespielt.count, season: saison.name })}
                </div>
              )}
              {clubParsed.issues.map((i) => (
                <div key={i.message} className={i.level === 'error' ? 'text-err' : 'text-warn'}>
                  {tm(i.message)}
                </div>
              ))}
            </div>
          )}
          {parsed && (
            <div className="text-sm text-muted">
              <b className="text-ink">{t('{n} Teams erkannt', { n: parsed.summary.total })}</b>
              {parsed.summary.withWarnings > 0 && <span className="text-warn"> · {t('{n} mit Hinweisen', { n: parsed.summary.withWarnings })}</span>}
              {parsed.summary.withErrors > 0 && <span className="text-err"> · {t('{n} nicht importierbar', { n: parsed.summary.withErrors })}</span>}
              <ul className="m-0 mt-2 pl-4 max-h-40 overflow-auto">
                {parsed.teams.slice(0, 12).map((it, i) => (
                  <li key={i}>
                    <b className={it.issues.some((x) => x.level === 'error') ? 'text-err' : 'text-ink'}>{it.team.name}</b>
                    <span className="text-dim"> — {it.team.players.map((p) => `${p.firstName} ${p.lastName}`.trim()).join(', ') || t('keine Spieler erkannt')}</span>
                    {it.issues[0] && <span className={it.issues[0].level === 'error' ? 'text-err' : 'text-warn'}> · {tm(it.issues[0].message)}</span>}
                  </li>
                ))}
                {parsed.teams.length > 12 && <li className="text-dim">{t('… und {n} weitere', { n: parsed.teams.length - 12 })}</li>}
              </ul>
            </div>
          )}
          <Preview lines={preview.lines} minutes={preview.minutes} courts={courtTotal} teams={teamCount} venues={venueLine} />
        </div>
      )}

      {step === STEPS.length - 1 && cloudPossible && (
        <label className="flex items-center gap-2 mt-6 text-sm cursor-pointer">
          <input type="checkbox" checked={cloud} onChange={(e) => setCloud(e.target.checked)} />
          {t('In meinem Konto sichern ({email}) — Cloud-Sync, Helfer-Geräte und Online-Anmeldung sofort möglich', { email: auth.user?.email ?? '' })}
        </label>
      )}
      <div className="flex items-center gap-2 mt-8">
        {step > 0 && (
          <Button onClick={() => setStep(step - 1)}>
            <ArrowLeft size={14} /> {t('Zurück')}
          </Button>
        )}
        <div className="flex-1" />
        {step < STEPS.length - 1 ? (
          <Button variant="primary" disabled={!canNext} onClick={() => setStep(step + 1)}>
            {t('Weiter')} <ArrowRight size={14} />
          </Button>
        ) : (
          <Button variant="primary" disabled={busy} onClick={finish}>
            <Check size={14} /> {t('Turnier anlegen')}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Modus-Kacheln als Radiogruppe (docs/notes/modus-wahl.md): zwei Spalten ab
 * `md`, ein Satz je Kachel, die ganze Erklärung des gewählten Modus einmal
 * darunter. Tastatur wie bei Radioknöpfen: Pfeile wandern und wählen, nur die
 * gewählte Kachel liegt in der Tab-Reihenfolge.
 */
function ModusWahl({ value, onChange }: { value: ModeKey; onChange: (m: ModeKey) => void }) {
  const t = useT();
  const gruppe = useRef<HTMLDivElement>(null);
  const gewaehlt = modeInfo(value);
  const springe = (richtung: 1 | -1 | 'anfang' | 'ende') => {
    const i = MODES_GEORDNET.findIndex((m) => m.key === value);
    const n = MODES_GEORDNET.length;
    const ziel = richtung === 'anfang' ? 0 : richtung === 'ende' ? n - 1 : (i + richtung + n) % n;
    const key = (MODES_GEORDNET[ziel] as ModeInfo).key;
    onChange(key);
    gruppe.current?.querySelector<HTMLElement>(`[data-modus="${key}"]`)?.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    const map: Record<string, 1 | -1 | 'anfang' | 'ende'> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: 'anfang', End: 'ende' };
    const r = map[e.key];
    if (!r) return;
    e.preventDefault();
    springe(r);
  };
  return (
    <div className="grid gap-3">
      <div ref={gruppe} role="radiogroup" aria-label={t('Modus')} className="grid grid-cols-1 md:grid-cols-2 gap-2" onKeyDown={onKey}>
        {MODES_GEORDNET.map((m) => {
          const aktiv = m.key === value;
          return (
            <button
              key={m.key}
              type="button"
              role="radio"
              aria-checked={aktiv}
              tabIndex={aktiv ? 0 : -1}
              data-modus={m.key}
              onClick={() => onChange(m.key)}
              className={cx(
                'relative text-left border-2 px-4 py-3 pr-10 cursor-pointer transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue',
                aktiv ? 'border-red bg-panel2' : 'border-line bg-panel hover:bg-panel2 hover:border-muted',
              )}
            >
              {aktiv && <Check size={16} className="absolute top-3 right-3 text-red" aria-hidden />}
              <div className="flex items-center gap-2 flex-wrap font-bold">
                <span>{t(m.name)}</span>
                {m.empfohlen && (
                  <Pill tone="live" className="text-[10px] px-1.5 py-px">
                    {t('Empfohlen')}
                  </Pill>
                )}
              </div>
              <div className="text-sm text-muted mt-0.5">{t(m.kurz)}</div>
            </button>
          );
        })}
      </div>
      <div className="bg-panel border border-line px-4 py-3 text-sm text-muted leading-relaxed" data-modus-hinweis>
        <div className="label mb-1">{t('So wird gespielt')}</div>
        {t(gewaehlt.description)}
      </div>
    </div>
  );
}

function Preview({ lines, minutes, courts, teams, venues }: { lines: string[]; minutes: number; courts: number; teams: number; venues?: string }) {
  const t = useT();
  return (
    <div className="bg-panel border border-line px-4 py-3 font-mono text-[12px] text-muted leading-relaxed">
      <div className="label mb-1">{teams > 0 ? t('Vorschau · {n} Teams · {c} Felder', { n: teams, c: courts }) : t('Vorschau · Annahme 16 Teams · {c} Felder', { c: courts })}</div>
      {venues && <div>{venues}</div>}
      {lines.map((l) => (
        <div key={l}>{l}</div>
      ))}
      <div className="text-ink">{t('Gesamt ca. {t} Spielzeit', { t: fmtMinutes(minutes) })}</div>
    </div>
  );
}
